import {useConfig} from '@aboutyou/config';
import {HeroBrandsSection_BrandElement_Products} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {TrackHeroBrandsSlider, useTrackOriginClick} from '@aboutyou/tracking';
import {Fade} from '@aboutyou/transitions';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  SponsoredWithBlurIcon,
  tabletAndUpLinaria,
  UnstyledLinkLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useSponsoredProductPopup} from '../../../../../backend-services/SponsoredProductPopup/utils/useSponsoredProductPopup';
import {FollowBrand} from '../../../../FollowBrand/FollowBrand';
import {useSplitFirstProductIds} from '../../../../hooks/useSplitFirstProductIds';
import {HeroContentEmpty} from './HeroContentEmpty';
import {HeroContentProductsList} from './HeroContentProductsList';
import {HeroContentProductsProps} from './HeroContentSwitch';

export const HeroContentView: React.FC<
  HeroContentProductsProps & {products: HeroBrandsSection_BrandElement_Products}
> = ({
  activeBrandIndex,
  brand,
  discoverMore,
  header,
  image,
  onAddToBasketCtaClick,
  onClick,
  onProductListSwipe,
  products,
  promotionTracker,
  streamPosition,
  uniqueKey,
  trackComponentConfig,
  sponsored,
}) => {
  const {device} = useConfig();
  const {onSponsoredClick} = useSponsoredProductPopup();
  const trackOriginClick = useTrackOriginClick();
  const history = useHistory();

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    products.link.url,
  );

  const handleLinkClick: React.MouseEventHandler = event => {
    const originId = (promotionTracker.locationId ??
      trackComponentConfig.location_id) as originClick;
    trackOriginClick(originId)();
    if (fullLink && fullLink !== linkWithoutFirstProductIds) {
      event.preventDefault();
      history.push(fullLink);
    }
  };

  if (!products.products.length) {
    return (
      <HeroContentEmpty
        image={image}
        brandLogo={header.lightLogo}
        brandName={header.fallbackLabel}
        cta={{label: discoverMore.headline, url: products.link.url}}
        onClick={onClick}
      />
    );
  }

  // Track which brands have been tracked to prevent duplicate impression events.
  // The hero brand slider renders a single card that updates its content based on the active brand.
  // We persist tracked impressions and products to prevent duplicate tracking when the component re-renders.
  const trackingUniqueKey = `${uniqueKey}-${brand.id}`;

  const handleSponsoredClick = (e: React.MouseEvent) => {
    onClick();
    onSponsoredClick({
      e,
      promotionId: promotionTracker.promotionId,
    });
  };

  const Wrapper = device === 'mobile' ? Container : React.Fragment;

  return (
    <>
      <StyledFollowBrand
        iconPosition={device === 'desktop' ? 'left' : 'right'}
        brand={{...brand, path: products.link.url}}
        onClick={onClick}
        promotionTracker={sponsored ? promotionTracker : undefined}
      />
      <SponsoredWrapper>
        <StyledSponsored
          iconPosition="left"
          color="White"
          label={sponsored?.label}
          onClick={handleSponsoredClick}
          skipRenderingText={!sponsored}
        />
      </SponsoredWrapper>
      <Wrapper>
        <StyledTrackPromotions
          promotion={{
            source: TrackingPromotionCreative.Brand,
            promotionTracker: promotionTracker
              ? {
                  ...promotionTracker,
                  destination: products.link.url,
                }
              : undefined,
            isSponsored: Boolean(sponsored),
          }}
          trackingKey={trackingUniqueKey}
          streamPosition={streamPosition}
          horizontalPosition={activeBrandIndex}
        >
          <Fade mounted duration={200} delay={250}>
            <BrandLogoWrapper
              onClick={handleLinkClick}
              to={linkWithoutFirstProductIds || products.link.url}
              aria-label={discoverMore.headline}
              data-testid="hero-brand-logo-link"
            >
              <StyledBrandLogo
                name={header?.fallbackLabel}
                image={header?.lightLogo}
                maxWidth="40%"
                maxHeight="60px"
                showTooltip={false}
              />
            </BrandLogoWrapper>
          </Fade>
        </StyledTrackPromotions>

        <HeroContentProductsList
          activeBrandIndex={activeBrandIndex}
          brand={brand}
          discoverMore={discoverMore}
          header={header}
          image={image}
          onAddToBasketCtaClick={onAddToBasketCtaClick}
          onClick={onClick}
          onProductListSwipe={onProductListSwipe}
          products={products}
          promotionTracker={promotionTracker}
          streamPosition={streamPosition}
          uniqueKey={uniqueKey}
          trackComponentConfig={trackComponentConfig}
        />
      </Wrapper>
    </>
  );
};

const Container = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const StyledTrackPromotions = styled(TrackHeroBrandsSlider)`
  display: flex;
  width: 100%;
  height: var(--cover-image-height);
  margin-top: 0;
  top: 0;
  z-index: 1;

  ${tabletAndUpLinaria(`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    width: 30%;
    height: 100%;
    margin: 0;
    padding: 0;
    font-size: ${FONT_SIZES.px32};
  `)}
`;

const BrandLogoWrapper = styled(UnstyledLinkLinaria)`
  display: flex;
  align-items: flex-end;
  width: 100%;
  height: 100%;

  ${tabletAndUpLinaria(`
    align-items: center;
  `)}
`;

const StyledBrandLogo = styled(BrandLogo)`
  height: fit-content;
  color: ${COLORS['Text.White']};
  font-size: ${FONT_SIZES.px28};

  ${tabletAndUpLinaria(`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 80%;
    font-size: ${FONT_SIZES.px32};
    z-index: 1;
  `)}
`;

const StyledFollowBrand = styled(FollowBrand)`
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 16px;
  z-index: 3;

  ${tabletAndUpLinaria(`
    padding: 20px;
    left: 0;
    right: unset;
  `)}
`;

const SponsoredWrapper = styled.div`
  position: absolute;
  top: 20px;
  left: 16px;
  z-index: 2;

  ${tabletAndUpLinaria(`
    max-width: calc(var(--desktop-image-width) - 3vw);
    top: 28px;
    left: 0;
    right: 48px;
  `)}
`;

const StyledSponsored = styled(SponsoredWithBlurIcon)`
  &&& {
    justify-content: flex-end;
  }
`;
